import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/profile");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(form.identifier.trim(), form.password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav(res.user.role === "admin" ? "/admin" : "/profile");
    } else {
      setError(res.error || "Login failed.");
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(rgba(0,180,255,0.5) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative">
          <BrandLogo size={44} variant="light" />
        </div>
        <div className="relative">
          <div className="text-sm tracking-[0.28em] uppercase font-mono-plex font-bold text-[#00b4ff]">Welcome back</div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight leading-tight">
            New Trading Education System.
          </h2>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
            Register untuk dapatkan Client ID.
          </p>
        </div>
        <div className="relative text-xs font-mono-plex tracking-widest text-slate-500 uppercase">
          Trading Secret Malaysia · Loyal Primus Partner
        </div>
      </div>

      {/* Right form */}
      <div className="p-6 md:p-12 flex flex-col justify-center">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" style={{ transition: "color 0.2s ease" }}>
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="lg:hidden mb-6"><BrandLogo /></div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-[#00b4ff]">Sign in</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Log in to your account</h1>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Email or Username</label>
              <input
                data-testid="login-identifier"
                required
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                placeholder="you@example.com"
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]"
              />
            </div>
            <div>
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Password</label>
              <input
                data-testid="login-password"
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Loyal Primus Client ID"
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex"
              />
            </div>

            {error && <div className="text-sm text-rose-600" data-testid="login-error">{error}</div>}

            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white font-display font-semibold px-6 py-3.5 rounded-full"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <LogIn size={16} /> {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-500">
            Belum register?{" "}
            <Link to="/" className="text-[#00b4ff] font-semibold hover:underline">Submit Loyal Primus details</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
